import type { HrZoneId } from './heartRateZones';
import { buildHrZones } from './heartRateZones';
import { normalizeCoachPlanText } from './runningCoach';

/**
 * Parses a free-form running plan written by Arnold or the user into a
 * sequence of timed phases that the live voice coach can follow turn by turn.
 *
 * The parser is intentionally lenient: most plans are 3-5 lines of prose like
 *
 *   - "5 minute warmup at easy Z2 pace"
 *   - "6 x 800m at threshold, 90 sec recovery jog"
 *   - "10 min cooldown"
 *
 * We extract structured phases when we can, but always fall back to a single
 * "free run" phase so the coach can still give pacing cues even if the plan is
 * unparseable.
 */

export type RunPhaseKind =
  | 'warmup'
  | 'cooldown'
  | 'recovery'
  | 'interval'
  | 'tempo'
  | 'easy'
  | 'long'
  | 'free';

export interface RunPhase {
  kind: RunPhaseKind;
  /** Index in the global ordered timeline (0-based). */
  index: number;
  /** Display label, e.g. "Warm-up", "Interval 3 of 6". */
  label: string;
  /** Total duration of the phase in seconds, when known. */
  durationSeconds?: number;
  /** Distance target in km, when the phase is distance-bounded. */
  distanceKm?: number;
  /** Target heart-rate zone for this phase, when stated. */
  targetZone?: HrZoneId;
  /** Target pace seconds per kilometer (lower bound = fastest). */
  paceSecondsPerKm?: number;
  /** Repetition number within a set (1-indexed) and total reps. */
  repInSet?: { rep: number; totalReps: number };
  /** Short cue spoken when the phase begins. */
  startCue: string;
  /** Short cue spoken near the end of the phase. */
  endCue?: string;
}

export interface ParsedRunStructure {
  phases: RunPhase[];
  /** Total planned duration of all phases that have a duration. */
  totalPlannedSeconds: number;
  /** True when the parser produced at least one structured (non-free) phase. */
  hasStructure: boolean;
}

const ZONE_REGEX = /\b(?:zone\s*([1-5])|z([1-5]))\b/i;
const DURATION_REGEX = /(\d+(?:\.\d+)?)\s*(min(?:ute)?s?|mins?|sec(?:ond)?s?|(?<![a-z])s(?!\w))/i;
// Do not use bare "m" — it matches the "m" in "min" (e.g. "10 min" → false 10 km).
const DISTANCE_REGEX = /(\d+(?:[.,]\d+)?)\s*(km|kilometers?|k\b|meters?)\b/i;
const PACE_REGEX = /(\d{1,2}):(\d{2})\s*(?:\/\s*km|min\s*\/\s*km|pace)?/i;
const MIN_PER_KM_REGEX = /(\d+(?:[.,]\d+)?)\s*min\s*(?:\/|per)\s*k(?:m|ilometer)?/i;
const REPS_REGEX = /(\d+)\s*[x×]\s*(\d+(?:[.,]\d+)?)\s*(km|m|min(?:utes?)?|sec(?:onds?)?|s\b)?/i;
const RECOVERY_REGEX = /(\d+)\s*(sec|s|min|m)?\s*(?:recovery|rest|easy jog|jog)/i;
/** e.g. "10km at 40min", "guided to do 10 km in 40 minutes" */
const GOAL_DISTANCE_TIME_REGEX =
  /(\d+(?:[.,]\d+)?)\s*(?:k(?:m)?|kilometers?)\s*(?:at|in|for|within|under)\s*(\d+(?:[.,]\d+)?)\s*(?:min(?:utes?)?|m\b)/i;

const parseDurationToSeconds = (raw: string): number | undefined => {
  if (/\bbpm\b/i.test(raw)) return undefined;
  const m = raw.match(DURATION_REGEX);
  if (!m) return undefined;
  const val = parseFloat(m[1]);
  const unit = m[2].toLowerCase();
  if (!Number.isFinite(val)) return undefined;
  if (unit.startsWith('sec') || unit === 's') return Math.round(val);
  return Math.round(val * 60);
};

const parseDistanceToKm = (raw: string): number | undefined => {
  const m = raw.match(DISTANCE_REGEX);
  if (!m) return undefined;
  const val = parseFloat(m[1].replace(',', '.'));
  if (!Number.isFinite(val)) return undefined;
  const unit = m[2].toLowerCase();
  if (unit.startsWith('km') || unit === 'k' || unit.startsWith('kilom')) return val;
  // assume meters
  return val / 1000;
};

const parsePaceToSec = (raw: string): number | undefined => {
  const colon = raw.match(PACE_REGEX);
  if (colon) return parseInt(colon[1], 10) * 60 + parseInt(colon[2], 10);
  const minKm = raw.match(MIN_PER_KM_REGEX);
  if (minKm) {
    const val = parseFloat(minKm[1].replace(",", "."));
    if (Number.isFinite(val) && val > 0) return Math.round(val * 60);
  }
  return undefined;
};

/** Target pace for the active phase (seconds per km), when stated on that phase line. */
export function resolveTargetPaceForPhase(
  phase: RunPhase | null | undefined,
  _planText?: string,
): number | undefined {
  if (phase?.paceSecondsPerKm && phase.paceSecondsPerKm > 0) {
    return phase.paceSecondsPerKm;
  }
  return undefined;
};

const parseZone = (raw: string): HrZoneId | undefined => {
  const m = raw.match(ZONE_REGEX);
  if (!m) return undefined;
  const num = parseInt(m[1] || m[2], 10);
  if (!Number.isFinite(num) || num < 1 || num > 5) return undefined;
  return `z${num}` as HrZoneId;
};

/** Lines that are plan metadata, not runnable phases. */
const isMetadataLine = (line: string): boolean => {
  const l = line.toLowerCase().trim();
  if (/^estimated\s+duration\b/.test(l)) return true;
  if (/^pacing\s+guide\s*:?\s*$/i.test(l)) return true;
  if (/^total\s+distance\b/.test(l)) return true;
  if (/^main\s+set\s*:?\s*$/i.test(l)) return true;
  if (/^workout\s+type\b/.test(l)) return true;
  if (/^notes?\s*:?\s*$/.test(l)) return true;
  // Title-only headers like "Easy Recovery Run" with no numbers.
  if (
    line.length < 48 &&
    /(?:run|jog|session|workout)$/i.test(l) &&
    !DURATION_REGEX.test(l) &&
    !DISTANCE_REGEX.test(l) &&
    !ZONE_REGEX.test(l) &&
    !REPS_REGEX.test(l)
  ) {
    return true;
  }
  return false;
};

/** Pacing-guide bullets and coaching notes — not separate runnable phases. */
const isPacingNoteLine = (line: string): boolean => {
  const l = line.toLowerCase().trim();
  if (/^pacing\s+guide/.test(l)) return true;
  if (/^why\s+this\s+works/.test(l)) return true;
  if (/^next\s+session/.test(l)) return true;
  if (/^focus\s+on\b/.test(l)) return true;
  if (/^notes?\s*:?\s*$/.test(l)) return true;
  // "Warm-up: Easy conversational" or "4km: Easy conversational (~5:30/km)" without duration/distance target
  if (/^(warm-?up|cool-?down|\d+\s*km)\s*:/i.test(line)) {
    const hasDuration = DURATION_REGEX.test(line);
    const hasDistance = DISTANCE_REGEX.test(line);
    if (!hasDuration && !hasDistance && /conversational|moderate effort|comfortably hard|very easy/i.test(l)) {
      return true;
    }
  }
  return false;
};

const classifyLine = (line: string): RunPhaseKind => {
  const l = line.toLowerCase();
  if (/warm\s*up|warm-up/.test(l)) return 'warmup';
  if (/cool\s*down|cool-down/.test(l)) return 'cooldown';
  if (/recovery|easy\s+jog|rest/.test(l) && !/recovery run/.test(l)) return 'recovery';
  if (/tempo|threshold/.test(l)) return 'tempo';
  if (/long\s+run/.test(l)) return 'long';
  if (/interval|[x×]\s*\d|repeat|repetition|sprints?/.test(l)) return 'interval';
  if (/easy|z[12]|zone\s*[12]/i.test(l)) return 'easy';
  return 'free';
};

const labelFor = (kind: RunPhaseKind, idx: number, rep?: { rep: number; totalReps: number }): string => {
  if (kind === 'warmup') return 'Warm-up';
  if (kind === 'cooldown') return 'Cool-down';
  if (kind === 'recovery') return 'Recovery';
  if (kind === 'tempo') return 'Tempo';
  if (kind === 'long') return 'Long run';
  if (kind === 'easy') return 'Easy run';
  if (kind === 'interval' && rep) return `Interval ${rep.rep} of ${rep.totalReps}`;
  if (kind === 'interval') return `Interval ${idx + 1}`;
  return 'Main set';
};

const blockNameForCoach = (kind: RunPhaseKind): string => {
  if (kind === 'warmup') return 'warm-up block';
  if (kind === 'cooldown') return 'cool-down block';
  if (kind === 'recovery') return 'recovery jog';
  if (kind === 'tempo') return 'tempo threshold block';
  if (kind === 'interval') return 'interval rep';
  if (kind === 'easy') return 'easy aerobic block';
  if (kind === 'long') return 'long-run segment';
  return 'main run block';
};

const describePhaseBrief = (phase: RunPhase): string => {
  if (phase.kind === 'interval' && phase.repInSet) {
    return `interval ${phase.repInSet.rep} of ${phase.repInSet.totalReps}`;
  }
  return blockNameForCoach(phase.kind);
};

/** Phase immediately after the given index in the plan timeline. */
export function getPhaseAfter(structure: ParsedRunStructure, phaseIndex: number): RunPhase | null {
  const i = structure.phases.findIndex((p) => p.index === phaseIndex);
  if (i < 0 || i + 1 >= structure.phases.length) return null;
  return structure.phases[i + 1];
};

export interface ActivePhaseCoachContext {
  label: string;
  /** Full spoken sentence for live coach (e.g. warm-up block, next phase). */
  summary: string;
  nextPhaseLabel?: string;
  phaseKind: RunPhaseKind;
}

/** Rich phase context for live coach — avoids generic "run phase" wording. */
export function describeActivePhaseForCoach(
  active: { phase: RunPhase; remainingSeconds: number | null },
  structure: ParsedRunStructure,
): ActivePhaseCoachContext {
  const { phase, remainingSeconds } = active;
  const next = getPhaseAfter(structure, phase.index);
  const zonePart = phase.targetZone ? `, targeting heart-rate ${phase.targetZone.toUpperCase()}` : '';
  const timePart =
    remainingSeconds != null && remainingSeconds > 0
      ? ` About ${formatDurationSpeech(remainingSeconds)} left in this block.`
      : '';
  const nextPart = next ? ` Next up: ${describePhaseBrief(next)}.` : '';

  let summary: string;
  if (phase.kind === 'interval' && phase.repInSet) {
    summary = `You are in interval ${phase.repInSet.rep} of ${phase.repInSet.totalReps}${zonePart}.${timePart}${nextPart}`;
  } else if (phase.kind === 'warmup') {
    summary = `You are in your warm-up block, easing in before the main work.${timePart}${nextPart}`;
  } else if (phase.kind === 'cooldown') {
    summary = `You are in your cool-down block. Ease off and let heart rate settle.${timePart}`;
  } else if (phase.kind === 'tempo') {
    summary = `You are in a tempo threshold block${zonePart}. Hold steady, comfortably hard effort.${timePart}${nextPart}`;
  } else if (phase.kind === 'recovery') {
    summary = `You are in a recovery jog between efforts. Keep it easy.${timePart}${nextPart}`;
  } else {
    summary = `You are in your ${blockNameForCoach(phase.kind)}${zonePart}.${timePart}${nextPart}`;
  }

  return {
    label: phase.label,
    summary: summary.replace(/\s+/g, ' ').trim(),
    nextPhaseLabel: next?.label,
    phaseKind: phase.kind,
  };
}

const cueFor = (
  kind: RunPhaseKind,
  options: { rep?: { rep: number; totalReps: number }; zone?: HrZoneId; pace?: number; durationSec?: number; distanceKm?: number },
): { start: string; end?: string } => {
  const zoneText = options.zone ? `, target ${options.zone.toUpperCase()}` : '';
  const paceText = options.pace
    ? `, around ${Math.floor(options.pace / 60)}:${String(options.pace % 60).padStart(2, '0')} per kilometer`
    : '';
  const durText = options.durationSec ? ` for ${formatDurationSpeech(options.durationSec)}` : '';
  const distText = options.distanceKm ? ` for ${options.distanceKm.toFixed(2).replace(/\.?0+$/, '')} kilometers` : '';

  if (kind === 'warmup') {
    return {
      start: `Starting warm-up${durText || distText}${zoneText}. Easy effort, get the legs moving.`,
      end: 'Warm-up nearly done. Get ready to lift the pace.',
    };
  }
  if (kind === 'cooldown') {
    return {
      start: `Beginning cool-down${durText || distText}. Slow, relaxed jog.`,
      end: 'Cool-down nearly complete. Great work today.',
    };
  }
  if (kind === 'recovery') {
    return {
      start: `Recovery${durText || distText}. Easy jog or walk, lower the heart rate.`,
      end: 'Recovery ending. Get ready for the next effort.',
    };
  }
  if (kind === 'tempo') {
    return {
      start: `Tempo block${durText || distText}${zoneText}${paceText}. Comfortably hard, steady breathing.`,
      end: 'Tempo block nearly done. Hold the pace through the end.',
    };
  }
  if (kind === 'interval') {
    const repPart = options.rep ? `Interval ${options.rep.rep} of ${options.rep.totalReps}` : 'Interval';
    return {
      start: `${repPart}${distText || durText}${zoneText}${paceText}. Drive the effort now.`,
      end: 'Almost there. Hold to the line.',
    };
  }
  if (kind === 'long') {
    return {
      start: `Long run${durText || distText}${zoneText}. Settle in to a sustainable rhythm.`,
    };
  }
  if (kind === 'easy') {
    return {
      start: `Easy run${durText || distText}${zoneText}. Conversational effort.`,
    };
  }
  return {
    start: `Continue running${durText || distText}${zoneText}${paceText}.`,
  };
};

const formatDurationSpeech = (sec: number): string => {
  if (sec >= 60) {
    const m = Math.round(sec / 60);
    return `${m} minute${m !== 1 ? 's' : ''}`;
  }
  return `${sec} second${sec !== 1 ? 's' : ''}`;
};

const expandIntervalSet = (line: string, indexStart: number): RunPhase[] | null => {
  const m = line.match(REPS_REGEX);
  if (!m) return null;
  const totalReps = parseInt(m[1], 10);
  if (!Number.isFinite(totalReps) || totalReps < 1 || totalReps > 30) return null;
  const repValue = parseFloat(m[2].replace(',', '.'));
  const repUnit = (m[3] || '').toLowerCase();

  const distanceKm = repUnit === 'km' ? repValue : repUnit === 'm' ? repValue / 1000 : undefined;
  const durationSeconds =
    repUnit.startsWith('min') ? Math.round(repValue * 60) : repUnit === 's' || repUnit.startsWith('sec') ? Math.round(repValue) : undefined;

  const zone = parseZone(line);
  const pace = parsePaceToSec(line);
  const recoveryMatch = line.match(RECOVERY_REGEX);
  let recoverySec: number | undefined;
  if (recoveryMatch) {
    const v = parseInt(recoveryMatch[1], 10);
    const u = (recoveryMatch[2] || 's').toLowerCase();
    recoverySec = u.startsWith('m') && u !== 'm' ? v * 60 : v;
    if (u === 'min') recoverySec = v * 60;
  }

  const out: RunPhase[] = [];
  for (let i = 0; i < totalReps; i++) {
    const rep = { rep: i + 1, totalReps };
    const cue = cueFor('interval', { rep, zone, pace, durationSec: durationSeconds, distanceKm });
    out.push({
      kind: 'interval',
      index: indexStart + out.length,
      label: labelFor('interval', indexStart + out.length, rep),
      durationSeconds,
      distanceKm,
      targetZone: zone,
      paceSecondsPerKm: pace,
      repInSet: rep,
      startCue: cue.start,
      endCue: cue.end,
    });
    if (recoverySec && i < totalReps - 1) {
      const recCue = cueFor('recovery', { durationSec: recoverySec });
      out.push({
        kind: 'recovery',
        index: indexStart + out.length,
        label: 'Recovery',
        durationSeconds: recoverySec,
        startCue: recCue.start,
        endCue: recCue.end,
      });
    }
  }
  return out;
};

const ZONE_DISTANCE_CHUNK =
  /(\d+(?:[.,]\d+)?)\s*(?:k(?:m)?|kilometers?)\s*(?:at\s*)?(?:zone\s*|z)\s*([1-5])/gi;

const ZONE_TIME_CHUNK =
  /(\d+)\s*min(?:ute)?s?\s*(?:at\s*)?(?:zone\s*|z)\s*([1-5])/gi;

const PACE_IN_LINE = /(\d{1,2}:\d{2})\s*(?:\/\s*k(?:m)?|pace|min)/i;

function formatPaceHint(secPerKm?: number): string | undefined {
  if (!secPerKm || !Number.isFinite(secPerKm) || secPerKm <= 0) return undefined;
  const m = Math.floor(secPerKm / 60);
  const s = Math.round(secPerKm % 60);
  return `${m}:${String(s).padStart(2, '0')}/km`;
}

function zoneForNumber(num: number, zones: import('./heartRateZones').HrZone[]): import('./heartRateZones').HrZone | undefined {
  const id = `z${num}` as HrZoneId;
  return zones.find((z) => z.id === id);
}

export type StructureRunGuideOptions = {
  ageYears?: number | null;
  maxHr?: number | null;
};

/** Turn shorthand into numbered coach-ready phases with HR ranges and effort cues. */
export function structureRunGuideFromInput(
  raw: string,
  opts?: StructureRunGuideOptions,
): string {
  try {
    return structureRunGuideFromInputInner(raw, opts);
  } catch (err) {
    console.warn('[structureRunGuideFromInput] failed:', err instanceof Error ? err.message : String(err));
    return normalizeCoachPlanText(raw).trim();
  }
}

function structureRunGuideFromInputInner(
  raw: string,
  opts?: StructureRunGuideOptions,
): string {
  const text = expandCommaSeparatedZoneSegments(normalizeCoachPlanText(raw).trim());
  if (!text) return '';

  const { zones } = buildHrZones({ ageYears: opts?.ageYears, maxHr: opts?.maxHr });
  const lines: string[] = [];

  const zoneDistance = [...text.matchAll(
    /(\d+(?:[.,]\d+)?)\s*(?:k(?:m)?|kilometers?)\s*(?:at\s*)?(?:@?\s*)?(?:(\d{1,2}:\d{2})\s*(?:\/\s*k(?:m)?|pace)?\s*)?(?:in\s*)?(?:zone\s*|z)\s*([1-5])/gi,
  )];
  if (zoneDistance.length >= 1) {
    zoneDistance.forEach((m, i) => {
      const km = m[1].replace(',', '.');
      const paceRaw = m[2];
      const zNum = parseInt(m[3], 10);
      const zone = zoneForNumber(zNum, zones);
      const paceSec = paceRaw ? parsePaceToSec(paceRaw) : undefined;
      const pacePart = paceSec ? ` @ ${formatPaceHint(paceSec)}` : '';
      const hrPart = zone
        ? ` (${zone.label}, ${zone.lowerBpm}–${zone.upperBpm} bpm — ${zone.description.split(',')[0]})`
        : ` (Z${zNum})`;
      lines.push(`${i + 1}. Run ${km} km in Zone ${zNum}${hrPart}${pacePart}`);
    });
    return lines.join('\n');
  }

  const zoneTime = [...text.matchAll(ZONE_TIME_CHUNK)];
  if (zoneTime.length >= 1) {
    zoneTime.forEach((m, i) => {
      const mins = m[1];
      const zNum = parseInt(m[2], 10);
      const zone = zoneForNumber(zNum, zones);
      const hrPart = zone
        ? ` (${zone.label}, ${zone.lowerBpm}–${zone.upperBpm} bpm)`
        : ` (Z${zNum})`;
      lines.push(`${i + 1}. ${mins} min in Zone ${zNum}${hrPart}`);
    });
    return lines.join('\n');
  }

  // "5 min warmup z1, 20 min z3, 5 min cooldown z1"
  const segmentParts = text.split(/\s*,\s*(?=\d|\bwarm|\bcool)/i).map((s) => s.trim()).filter(Boolean);
  if (segmentParts.length >= 2) {
    segmentParts.forEach((part, i) => {
      const lower = part.toLowerCase();
      const isWarm = /warm/.test(lower);
      const isCool = /cool/.test(lower);
      const dur = part.match(/(\d+)\s*min/);
      const dist = part.match(/(\d+(?:[.,]\d+)?)\s*k(?:m)?/i);
      const zoneM = part.match(/(?:zone\s*|z)\s*([1-5])/i);
      const paceM = part.match(PACE_IN_LINE);
      const zNum = zoneM ? parseInt(zoneM[1], 10) : isWarm ? 1 : isCool ? 1 : undefined;
      const zone = zNum ? zoneForNumber(zNum, zones) : undefined;
      const label = isWarm ? 'Warm-up' : isCool ? 'Cool-down' : `Block ${i + 1}`;
      const target =
        dur ? `${dur[1]} min` : dist ? `${dist[1].replace(',', '.')} km` : part.slice(0, 40);
      const hrPart = zone ? ` — ${zone.label}, ${zone.lowerBpm}–${zone.upperBpm} bpm` : '';
      const pacePart = paceM ? ` @ ${paceM[1]}/km` : '';
      lines.push(`${i + 1}. ${label}: ${target}${hrPart}${pacePart}`);
    });
    if (lines.length >= 2) return lines.join('\n');
  }

  // Single goal: "10km at 45 min" or "5k easy"
  const goalRun = parseGoalDistanceAndTime(text);
  if (goalRun) {
    const pace = formatPaceHint(goalRun.paceSecondsPerKm);
    return [
      `1. Target run: ${goalRun.distanceKm} km in ${Math.round(goalRun.durationSeconds / 60)} min`,
      pace ? `   Target pace: ${pace} (steady effort throughout)` : '',
      `   Stay controlled — Arnold will coach pace and effort live.`,
    ]
      .filter(Boolean)
      .join('\n');
  }

  return text;
}

/** Split comma-separated zone/distance segments onto separate lines before parsing. */
function expandCommaSeparatedZoneSegments(text: string): string {
  if (!/(?:zone\s*[1-5]|z[1-5])/i.test(text)) return text;
  if (text.includes('\n')) return text;
  const parts = text.split(/\s*,\s*(?=\d)/).map((s) => s.trim()).filter(Boolean);
  if (parts.length <= 1) return text;
  return parts.join('\n');
}

/** Strip leading list markers like "1. " or "2) ". */
const stripLinePrefix = (line: string): string => line.replace(/^\d+[.)]\s*/, '').trim();

/**
 * Split a plan into one phase per "line" (sentence / list item) and try to
 * extract duration, distance, target zone, pace, and rep count from each.
 */
function parseRunPlanInner(plan: unknown): ParsedRunStructure {
  const text = expandCommaSeparatedZoneSegments(normalizeCoachPlanText(plan).trim());
  if (!text) {
    return { phases: [], totalPlannedSeconds: 0, hasStructure: false };
  }

  const goalRun = parseGoalDistanceAndTime(text);
  if (goalRun && !/[\n\r]|(?:\.\s+[A-Z])|;|\u2022|\s+-\s+/i.test(text)) {
    const cue = cueFor('tempo', {
      pace: goalRun.paceSecondsPerKm,
      distanceKm: goalRun.distanceKm,
    });
    return {
      phases: [
        {
          kind: 'tempo',
          index: 0,
          label: 'Target run',
          distanceKm: goalRun.distanceKm,
          paceSecondsPerKm: goalRun.paceSecondsPerKm,
          startCue: cue.start,
          endCue: cue.end,
        },
      ],
      totalPlannedSeconds: goalRun.durationSeconds,
      hasStructure: true,
    };
  }

  const lines = text
    .split(/[\n\r]+/)
    .flatMap((block) =>
      block.split(/(?:(?<=[a-z0-9])\.\s+(?=[A-Z]))|;\s*|\u2022\s*|\s+-\s+/g),
    )
    .map((line) => stripLinePrefix(line.trim()))
    .filter(Boolean);

  const phases: RunPhase[] = [];
  let cursor = 0;

  for (const line of lines) {
    if (isMetadataLine(line) || isPacingNoteLine(line)) continue;

    const expanded = expandIntervalSet(line, cursor);
    if (expanded && expanded.length > 0) {
      phases.push(...expanded);
      cursor = phases.length;
      continue;
    }

    const kind = classifyLine(line);
    const durationSeconds = parseDurationToSeconds(line);
    const distanceKm = parseDistanceToKm(line);
    const zone = parseZone(line);
    let pace = parsePaceToSec(line);
    if (kind === 'free' && !durationSeconds && !distanceKm && !zone) continue;
    // Bare "recovery" labels without duration are pacing notes, not phases.
    if (kind === 'recovery' && !durationSeconds && !distanceKm) continue;

    const lineGoal = kind === 'free' ? parseGoalDistanceAndTime(line) : null;
    if (lineGoal) {
      const cue = cueFor('tempo', {
        pace: lineGoal.paceSecondsPerKm,
        distanceKm: lineGoal.distanceKm,
      });
      phases.push({
        kind: 'tempo',
        index: cursor,
        label: 'Target run',
        distanceKm: lineGoal.distanceKm,
        paceSecondsPerKm: lineGoal.paceSecondsPerKm,
        startCue: cue.start,
        endCue: cue.end,
      });
      cursor = phases.length;
      continue;
    }

    if (!pace && distanceKm && durationSeconds) {
      pace = paceFromDistanceAndDuration(distanceKm, durationSeconds);
    }

    const cue = cueFor(kind, { zone, pace, durationSec: durationSeconds, distanceKm });
    phases.push({
      kind,
      index: cursor,
      label: labelFor(kind, cursor),
      durationSeconds,
      distanceKm,
      targetZone: zone,
      paceSecondsPerKm: pace,
      startCue: cue.start,
      endCue: cue.end,
    });
    cursor = phases.length;
  }

  if (phases.length === 0) {
    phases.push({
      kind: 'free',
      index: 0,
      label: 'Run',
      startCue: 'Free run. Keep a steady rhythm and listen to your body.',
    });
  }

  const totalPlannedSeconds = phases.reduce((acc, p) => acc + (p.durationSeconds || 0), 0);
  const hasStructure = phases.some(
    (p) =>
      p.durationSeconds ||
      p.distanceKm ||
      p.targetZone ||
      p.paceSecondsPerKm ||
      (p.kind !== 'free' && (p.durationSeconds || p.distanceKm || p.targetZone)),
  );
  return { phases, totalPlannedSeconds, hasStructure };
}

export function parseRunPlan(plan: unknown): ParsedRunStructure {
  try {
    return parseRunPlanInner(plan);
  } catch (err) {
    console.warn('[parseRunPlan] failed:', err);
    const text = normalizeCoachPlanText(plan).trim();
    return {
      phases: [
        {
          kind: 'free',
          index: 0,
          label: 'Run',
          startCue: text
            ? 'Follow your plan. I will guide effort and pacing throughout.'
            : 'Free run. Keep a steady rhythm and listen to your body.',
        },
      ],
      totalPlannedSeconds: 0,
      hasStructure: false,
    };
  }
}

/**
 * Decide which phase the runner is currently in based on elapsed time and
 * distance. Returns the active phase + remaining seconds (when knowable).
 */
/** Default dwell per phase when the plan has structure but a line has no duration/distance. */
const DEFAULT_PHASE_DWELL_SEC = 180;

const effectivePhaseSeconds = (
  phase: RunPhase,
  structure: ParsedRunStructure,
): number => {
  const explicit = phase.durationSeconds ?? 0;
  if (explicit > 0) return explicit;
  if ((phase.distanceKm ?? 0) > 0) return 0;

  if (!structure.hasStructure || structure.phases.length <= 1) return 0;

  const unspecified = structure.phases.filter(
    (p) => !(p.durationSeconds && p.durationSeconds > 0) && !(p.distanceKm && p.distanceKm > 0),
  ).length;
  if (unspecified <= 0) return DEFAULT_PHASE_DWELL_SEC;

  if (structure.totalPlannedSeconds > 0) {
    return Math.max(60, Math.round(structure.totalPlannedSeconds / unspecified));
  }
  return DEFAULT_PHASE_DWELL_SEC;
};

export function getActivePhase(
  structure: ParsedRunStructure,
  elapsedSeconds: number,
  distanceKm: number,
): { phase: RunPhase; remainingSeconds: number | null; progress: number } | null {
  if (structure.phases.length === 0) return null;

  let accSec = 0;
  let accKm = 0;
  for (const phase of structure.phases) {
    const phaseSec = effectivePhaseSeconds(phase, structure);
    const phaseKm = phase.distanceKm ?? 0;

    // Time-bounded phase (explicit or estimated dwell)
    if (phaseSec > 0) {
      const end = accSec + phaseSec;
      if (elapsedSeconds < end) {
        const into = elapsedSeconds - accSec;
        return {
          phase,
          remainingSeconds: Math.max(0, Math.round(end - elapsedSeconds)),
          progress: phaseSec > 0 ? into / phaseSec : 0,
        };
      }
      accSec = end;
      continue;
    }

    // Distance-bounded phase
    if (phaseKm > 0) {
      const endKm = accKm + phaseKm;
      if (distanceKm < endKm) {
        const intoKm = distanceKm - accKm;
        return {
          phase,
          remainingSeconds: null,
          progress: phaseKm > 0 ? intoKm / phaseKm : 0,
        };
      }
      accKm = endKm;
      continue;
    }

    // Single free-form phase with no bounds
    if (structure.phases.length === 1) {
      return { phase, remainingSeconds: null, progress: 0 };
    }
  }

  // Past the end of the plan — keep the last phase active so the coach can
  // still give freeform encouragement.
  const last = structure.phases[structure.phases.length - 1];
  return { phase: last, remainingSeconds: 0, progress: 1 };
}

/** Natural-language list join: ["a", "b", "c"] → "a, b, and c". */
const joinList = (parts: string[]): string => {
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0];
  if (parts.length === 2) return `${parts[0]} and ${parts[1]}`;
  return `${parts.slice(0, -1).join(', ')}, and ${parts[parts.length - 1]}`;
};

const speakDuration = (sec?: number): string => {
  if (!sec || sec <= 0) return '';
  if (sec < 60) return `${sec} second${sec !== 1 ? 's' : ''}`;
  const m = Math.round(sec / 60);
  return `${m} minute${m !== 1 ? 's' : ''}`;
};

const speakDistance = (km?: number): string => {
  if (!km || km <= 0) return '';
  if (km < 1) return `${Math.round(km * 1000)} meter${Math.round(km * 1000) !== 1 ? 's' : ''}`;
  // Trim trailing .0 ("5 kilometers" not "5.0 kilometers")
  const v = km.toFixed(km < 10 ? 1 : 0).replace(/\.0$/, '');
  return `${v} kilometer${v === '1' ? '' : 's'}`;
};

const speakZone = (zone?: HrZoneId): string => {
  if (!zone) return '';
  const num = zone.replace('z', '');
  return `zone ${num}`;
};

const speakPace = (sec?: number): string => {
  if (!sec || sec <= 0) return '';
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  if (m <= 0) return `${s} seconds per kilometer pace`;
  if (s === 0) return `${m} minute pace per kilometer`;
  return `${m} ${s.toString().padStart(2, '0')} pace per kilometer`;
};

/** Returns the "at zone X" / "at Y pace" / "at zone X, Y pace" suffix. */
const speakIntensity = (phase: RunPhase): string => {
  const zone = speakZone(phase.targetZone);
  const pace = speakPace(phase.paceSecondsPerKm);
  if (zone && pace) return ` at ${zone}, ${pace}`;
  if (zone) return ` at ${zone}`;
  if (pace) return ` at ${pace}`;
  return '';
};

const describeSinglePhase = (phase: RunPhase): string => {
  const dur = speakDuration(phase.durationSeconds);
  const dist = speakDistance(phase.distanceKm);
  const intensity = speakIntensity(phase);
  const block = dur || dist;

  switch (phase.kind) {
    case 'warmup':
      return block ? `${block} warm-up${intensity}` : `a warm-up${intensity}`;
    case 'cooldown':
      return block ? `${block} cool-down` : `a cool-down`;
    case 'recovery':
      return block ? `${block} recovery` : 'a short recovery';
    case 'tempo':
      return block ? `${block} tempo block${intensity}` : `a tempo block${intensity}`;
    case 'easy':
      return block ? `${block} easy run${intensity}` : `an easy run${intensity}`;
    case 'long':
      return block ? `${block} long run${intensity}` : `a long run${intensity}`;
    case 'interval':
      return block ? `${block} interval${intensity}` : `an interval${intensity}`;
    default:
      return block ? `${block} of running${intensity}` : '';
  }
};

/**
 * Build a short, speakable summary of the plan structure for the coach intro.
 * Groups consecutive intervals with the same rep count into "6 by 800 meter
 * intervals at zone 4 with 90 seconds recovery".
 */
export function buildPlanSummarySpeech(structure: ParsedRunStructure): string {
  if (!structure.hasStructure || structure.phases.length === 0) return '';

  const hasIntervals = structure.phases.some((p) => p.kind === 'interval');
  if (!hasIntervals) {
    const parts: string[] = [];
    const seen = new Set<string>();
    for (const phase of structure.phases) {
      if (phase.kind === 'free') continue;
      if (phase.kind === 'recovery' && !(phase.durationSeconds && phase.durationSeconds > 0)) continue;
      const d = describeSinglePhase(phase);
      if (!d) continue;
      const key = d.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      parts.push(d);
    }
    return joinList(parts);
  }

  const parts: string[] = [];
  let i = 0;
  while (i < structure.phases.length) {
    const phase = structure.phases[i];

    // Standalone recovery (not between intervals) — skip in the high-level summary.
    if (phase.kind === 'recovery' && i > 0 && i < structure.phases.length - 1) {
      i++;
      continue;
    }

    // Group consecutive intervals with same totalReps.
    if (phase.kind === 'interval' && phase.repInSet) {
      const totalReps = phase.repInSet.totalReps;
      const distOrDur = speakDistance(phase.distanceKm) || speakDuration(phase.durationSeconds);
      const intensity = speakIntensity(phase);
      const intervalLabel = distOrDur
        ? `${totalReps} by ${distOrDur} interval${totalReps !== 1 ? 's' : ''}${intensity}`
        : `${totalReps} interval${totalReps !== 1 ? 's' : ''}${intensity}`;

      // Look for recovery between intervals in the same set to mention it.
      let recoveryDesc = '';
      let j = i + 1;
      while (j < structure.phases.length) {
        const p = structure.phases[j];
        if (p.kind === 'interval' && p.repInSet?.totalReps === totalReps) { j++; continue; }
        if (p.kind === 'recovery') {
          if (!recoveryDesc) {
            const r = speakDuration(p.durationSeconds);
            if (r) recoveryDesc = ` with ${r} recovery between`;
          }
          j++;
          continue;
        }
        break;
      }

      parts.push(`${intervalLabel}${recoveryDesc}`);
      i = j;
      continue;
    }

    const desc = describeSinglePhase(phase);
    if (desc) parts.push(desc);
    i++;
  }

  return joinList(parts);
}

/**
 * Coach intro: one pass over structural phases only (no pacing-guide bullets).
 */
export function buildPlanIntroSpeech(planText: string, structure: ParsedRunStructure): string {
  const summary = buildPlanSummarySpeech(structure);
  if (summary) return summary;

  const lines = (planText || '')
    .split(/[\n\r]+/)
    .map((l) => l.replace(/^#+\s*/, '').replace(/^[-•*]\s*/, '').trim())
    .filter((l) => l && !isMetadataLine(l) && !isPacingNoteLine(l));
  return joinList(lines.slice(0, 4).map((l) => l.replace(/\bZ(\d)\b/gi, 'zone $1').slice(0, 90)));
}
